// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FilterOption } from '@/components/filter-toggle-group';
import { ChargeStatus } from '@/constants/charge-status';
import { DateRangeShortcut } from '@/constants/date-range-shortcut';
import {
  DoNotDisturbAltOutlined,
  ListRounded,
  PriceCheckOutlined,
  ReceiptLongOutlined,
  ScheduleOutlined,
} from '@/constants/hardfin-standard-icons';
import { DateTime } from 'luxon';
import { createElement } from 'react';

export const STATUS_CHIP_CONFIG: Record<
  ChargeStatus,
  {
    label: string;
    variant: 'outlined' | 'filled';
    color: 'default' | 'primary' | 'success' | 'error';
  }
> = {
  PLANNED: { label: 'Planned', variant: 'outlined', color: 'default' },
  BILLABLE: { label: 'Billable', variant: 'filled', color: 'primary' },
  INVOICED: { label: 'On invoice', variant: 'outlined', color: 'success' },
  CANCELLED: { label: 'Cancelled', variant: 'outlined', color: 'error' },
};

// Maps each charge status to a MUI theme palette token that
// ChargeSummaryCards forwards to PrimaryMetric.color. Never put hex
// values here — tokens resolve against the active theme.
export const CARD_COLOR_TOKENS: Record<ChargeStatus, string> = {
  PLANNED: 'text.secondary',
  BILLABLE: 'primary.main',
  INVOICED: 'success.main',
  CANCELLED: 'error.main',
};

export const STATUS_FILTER_OPTIONS: FilterOption<ChargeStatus | 'all'>[] = [
  {
    value: 'all',
    label: 'All',
    icon: createElement(ListRounded, { fontSize: 'small' }),
  },
  {
    value: 'PLANNED',
    label: 'Planned',
    icon: createElement(ScheduleOutlined, { fontSize: 'small' }),
  },
  {
    value: 'BILLABLE',
    label: 'Billable',
    icon: createElement(PriceCheckOutlined, { fontSize: 'small' }),
  },
  {
    value: 'INVOICED',
    label: 'On invoice',
    icon: createElement(ReceiptLongOutlined, { fontSize: 'small' }),
  },
  {
    value: 'CANCELLED',
    label: 'Cancelled',
    icon: createElement(DoNotDisturbAltOutlined, { fontSize: 'small' }),
  },
];

export const PAGE_SIZE = 20;

// Column widths are expressed as percentages so the whole row sums to 100%.
// Adjusting one column moves width only within the chosen exchange pair
// rather than silently compressing others (fixed-px columns used to leak
// space into neighbors when the row oversubscribed 100%).
export const CHARGE_STATUS_COL_WIDTH = '7%';
export const CHARGE_ACTIONS_COL_WIDTH = '4%';
export const CHARGE_BILLABLE_DATE_COL_WIDTH = '10%';

export const BILL_DATE_SHORTCUTS: DateRangeShortcut[] = [
  {
    label: 'Last 90 days',
    getDateRange: (today) => [
      today.minus({ days: 90 }),
      today.minus({ days: 1 }),
    ],
  },
  {
    label: 'Last 60 days',
    getDateRange: (today) => [
      today.minus({ days: 60 }),
      today.minus({ days: 1 }),
    ],
  },
  {
    label: 'Last 30 days',
    getDateRange: (today) => [
      today.minus({ days: 30 }),
      today.minus({ days: 1 }),
    ],
  },
  {
    label: 'Next 30 days',
    getDateRange: (today) => [today, today.plus({ days: 30 })],
  },
  {
    label: 'Next 60 days',
    getDateRange: (today) => [today, today.plus({ days: 60 })],
  },
  {
    label: 'Next 90 days',
    getDateRange: (today) => [today, today.plus({ days: 90 })],
  },
];

export function formatChargeTimestamp(dt: DateTime): string {
  return dt.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
}
